import type { ComponentType } from 'react';

import {
  ProductLogoSmallAttendanceCore,
  ProductLogoSmallContractorCore,
  ProductLogoSmallITOperationCore,
  ProductLogoSmallLaborCore,
} from '@/components/Logo';
import type { ApiSearchQuery } from '@/lib/api/api-type-helper';
import { db } from '@/mocks/db/schema';
import type {
  IndividualPortalRequestForm,
  IndividualPortalRequestFormCategory,
} from '@/mocks/db/schema';

type ProductLogoComponent = ComponentType<{ size?: number; color?: string }>;

export type OwnerProduct = NonNullable<
  ApiSearchQuery<'/v2/self-service/operation-packages', 'get'>['ownerProduct']
>;

export interface RequestFormCategoryGroup {
  category: IndividualPortalRequestFormCategory;
  label: string;
  LogoComponent: ProductLogoComponent;
  /**
   * `null` の場合、その category には Individual から申請可能な Operation Package が無いため
   * 一覧 API は呼ばずに全件非クリッカブルで描画する。
   */
  ownerProduct: OwnerProduct | null;
  items: IndividualPortalRequestForm[];
}

export interface RequestFormsLoaderData {
  groups: RequestFormCategoryGroup[];
}

const CATEGORY_ORDER: IndividualPortalRequestFormCategory[] = [
  'attendance',
  'labor',
  'it',
  'contract',
];

const CATEGORY_META: Record<
  IndividualPortalRequestFormCategory,
  {
    label: string;
    LogoComponent: ProductLogoComponent;
    ownerProduct: OwnerProduct | null;
  }
> = {
  attendance: {
    label: '勤怠',
    LogoComponent: ProductLogoSmallAttendanceCore,
    ownerProduct: null,
  },
  labor: {
    label: '労務',
    LogoComponent: ProductLogoSmallLaborCore,
    ownerProduct: 'HRF_LABOR_CORE',
  },
  it: {
    label: 'IT',
    LogoComponent: ProductLogoSmallITOperationCore,
    ownerProduct: 'IT_OPERATION_CORE',
  },
  contract: {
    label: '業務契約',
    LogoComponent: ProductLogoSmallContractorCore,
    ownerProduct: 'HRF_CONTRACTOR_CORE',
  },
};

export async function loadRequestFormsData(): Promise<RequestFormsLoaderData> {
  const items = await db.individualPortalRequestForms
    .orderBy('sortOrder')
    .toArray();

  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_META[category].label,
    LogoComponent: CATEGORY_META[category].LogoComponent,
    ownerProduct: CATEGORY_META[category].ownerProduct,
    items: items.filter((item) => item.category === category),
  })).filter((group) => group.items.length > 0);

  return { groups };
}
